import React, { useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCameraRetro,
  FaExclamationTriangle,
  FaImages,
} from "react-icons/fa";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiImage,
  FiMaximize2,
  FiX,
} from "react-icons/fi";

import { getAllGalleries } from "../../services/gallerieServices";
import { useUser } from "../Home/UserContext";
import Reveal from "../../components/Reveal/Reveal";
import { toAbsolutePhotoUrl as toAbsPhoto } from "../../services/albumPhotoServices";

const FALLBACK = "/img/no-image.jpg";
const MAX_HOME_ALBUMS = 3;
const MAX_PREVIEW_PHOTOS = 12;

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function sanitizeHtml(html = "") {
  return DOMPurify.sanitize(html || "", {
    USE_PROFILES: {
      html: true,
    },
    FORBID_ATTR: ["style"],
  });
}

function stripHtml(html = "") {
  if (typeof window === "undefined") {
    return String(html || "").replace(/<[^>]*>/g, "").trim();
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
}

function isActive(value) {
  if (value === undefined || value === null || value === "") return true;
  return value === true || value === 1 || value === "1";
}

function resolvePhoto(url) {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return FALLBACK;

  return toAbsPhoto(cleanUrl) || FALLBACK;
}

function GallerySkeletonCard() {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="h-64 animate-pulse bg-slate-200 dark:bg-slate-800" />

      <div className="space-y-4 p-5">
        <div className="h-5 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="col-span-full rounded-[2rem] border border-dashed border-slate-300 bg-white/70 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaImages />
      </div>

      <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Aucun album photo disponible
      </h3>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        Les albums du club apparaîtront ici dès qu’ils seront publiés dans
        l’administration.
      </p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="col-span-full rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
      <div className="flex items-center justify-center gap-3">
        <FaExclamationTriangle />
        {message}
      </div>
    </div>
  );
}

function GalleryCard({
  gallery,
  photos,
  cover,
  index,
  user,
  onPreview,
  onOpenAlbum,
}) {
  const descriptionText = stripHtml(gallery?.description || "");
  const previewPhotos = photos.slice(0, 4);

  return (
    <Reveal variant="zoom" delay={index * 90}>
      <article className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900">
        <button
          type="button"
          onClick={() => onPreview(gallery)}
          className="relative block h-72 w-full overflow-hidden text-left"
          aria-label={`Prévisualiser l'album ${gallery?.titre || ""}`}
          title="Prévisualiser l’album"
        >
          <img
            src={cover}
            alt={gallery?.titre || "Album photo"}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
            onError={(event) => {
              event.currentTarget.src = FALLBACK;
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/25 opacity-0 transition duration-500 group-hover:opacity-100" />

          <div className="absolute left-4 top-4 z-20 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/95 px-4 py-2 text-xs font-extrabold text-slate-950 shadow-xl backdrop-blur-xl">
            <FiImage className="text-primary" />
            {photos.length} photo{photos.length > 1 ? "s" : ""}
          </div>

          <div className="absolute right-4 top-4 z-20 flex h-12 w-12 items-center justify-center rounded-full border border-white/40 bg-white/95 text-slate-950 shadow-2xl backdrop-blur-xl transition group-hover:bg-primary group-hover:text-white">
            <FiEye className="text-lg" />
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-5">
            {/* <p className="mb-3 inline-flex rounded-full border border-white/15 bg-white/15 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-white/75 backdrop-blur-xl">
              Album photo
            </p> */}

            <h3 className="font-barlow text-2xl font-extrabold leading-tight text-white drop-shadow-lg">
              {gallery?.titre || "Album du club"}
            </h3>
          </div>
        </button>

        <div className="flex flex-1 flex-col p-5 pb-7">
          {descriptionText ? (
            <p className="line-clamp-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
              {descriptionText}
            </p>
          ) : (
            <p className="text-sm italic leading-7 text-slate-500 dark:text-slate-400">
              Revivez les moments forts du club à travers cet album.
            </p>
          )}

          {previewPhotos.length > 1 && (
            <div className="mt-5 grid grid-cols-4 gap-2">
              {previewPhotos.map((photo, photoIndex) => (
                <button
                  key={photo?.id || `${gallery?.id}-${photoIndex}`}
                  type="button"
                  onClick={() => onPreview(gallery, photoIndex)}
                  className="relative h-16 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 transition hover:-translate-y-0.5 hover:shadow dark:border-white/10 dark:bg-slate-800"
                  aria-label={`Ouvrir la photo ${photoIndex + 1}`}
                >
                  <img
                    src={resolvePhoto(photo?.photo_url)}
                    alt={photo?.titre || gallery?.titre || "Photo"}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.src = FALLBACK;
                    }}
                  />
                </button>
              ))}
            </div>
          )}

          <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5">
            <button
              type="button"
              onClick={() => onPreview(gallery)}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-5 py-3 text-sm font-bold text-primary transition hover:-translate-y-0.5 hover:bg-primary hover:text-white"
            >
              Prévisualiser
              <FiMaximize2 className="text-sm" />
            </button>

            {user && (
              <button
                type="button"
                onClick={() => onOpenAlbum(gallery.id)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                Voir l’album
                <FaArrowRight className="text-xs" />
              </button>
            )}
          </div>
        </div>

        <div
          aria-hidden
          className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-90 transition group-hover:opacity-100"
        />
      </article>
    </Reveal>
  );
}

function AlbumPreviewModal({
  gallery,
  photos,
  user,
  onClose,
  onOpenAlbum,
  onOpenLightbox,
}) {
  const hasMorePhotos = photos.length > MAX_PREVIEW_PHOTOS;
  const previewPhotos = photos.slice(0, MAX_PREVIEW_PHOTOS);
  const safeDescription = sanitizeHtml(gallery?.description || "");

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!gallery) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/75 px-3 py-4 backdrop-blur-sm sm:px-6 sm:py-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 p-4 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/95 sm:p-6">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:text-primary dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
            aria-label="Fermer l’album"
          >
            <FiX className="text-xl" />
          </button>

          <div className="pr-14">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
              <FaCameraRetro />
              Prévisualisation
            </div>

            <h3 className="mt-4 font-barlow text-2xl font-extrabold text-slate-950 dark:text-white sm:text-3xl">
              {gallery?.titre || "Album photo"}
            </h3>

            <p className="mt-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
              {photos.length} photo{photos.length > 1 ? "s" : ""}
              {hasMorePhotos
                ? ` — ${MAX_PREVIEW_PHOTOS} affichées en aperçu`
                : ""}
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {safeDescription && (
            <div
              className={cn(
                "mb-6 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5",
                "prose prose-slate max-w-none text-sm leading-7 dark:prose-invert",
                "dark:[&_strong]:!text-white dark:[&_b]:!text-white"
              )}
              dangerouslySetInnerHTML={{
                __html: safeDescription,
              }}
            />
          )}

          {previewPhotos.length === 0 ? (
            <div className="rounded-[2rem] border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm italic text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
              Aucune photo dans cet album.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {previewPhotos.map((photo, index) => (
                <button
                  key={photo?.id || index}
                  type="button"
                  onClick={() => onOpenLightbox(index)}
                  className="group relative h-36 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm transition hover:-translate-y-1 hover:shadow-lg dark:border-white/10 dark:bg-slate-800 sm:h-44"
                  aria-label={`Ouvrir la photo ${index + 1}`}
                >
                  <img
                    src={resolvePhoto(photo?.photo_url)}
                    alt={photo?.titre || gallery?.titre || "Photo"}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.src = FALLBACK;
                    }}
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />

                  <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg-white/90 text-slate-950 opacity-0 shadow-lg backdrop-blur transition group-hover:opacity-100">
                    <FiMaximize2 />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/95 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
              Pour éviter les problèmes d’affichage sur mobile, la
              prévisualisation est volontairement limitée.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
              >
                Fermer
              </button>

              {user && (
                <button
                  type="button"
                  onClick={() => onOpenAlbum(gallery.id)}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
                >
                  Voir toutes les photos
                  <FaArrowRight className="text-xs" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Lightbox({
  gallery,
  photos,
  index,
  onClose,
  onPrev,
  onNext,
}) {
  const currentPhoto = photos[index];

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") onPrev();
      if (event.key === "ArrowRight") onNext();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, onPrev, onNext]);

  if (!currentPhoto) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/90 p-3 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-6xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute -right-2 -top-2 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/90 text-slate-900 shadow-xl transition hover:text-primary"
          aria-label="Fermer"
        >
          <FiX className="text-xl" />
        </button>

        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={onPrev}
              className="absolute left-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-xl transition hover:text-primary sm:left-4"
              aria-label="Photo précédente"
            >
              <FiChevronLeft className="text-xl" />
            </button>

            <button
              type="button"
              onClick={onNext}
              className="absolute right-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-xl transition hover:text-primary sm:right-4"
              aria-label="Photo suivante"
            >
              <FiChevronRight className="text-xl" />
            </button>
          </>
        )}

        <img
          src={resolvePhoto(currentPhoto?.photo_url)}
          alt={currentPhoto?.titre || gallery?.titre || "Photo"}
          className="max-h-[82vh] w-full rounded-[2rem] border border-white/15 bg-black/20 object-contain shadow-2xl"
          onError={(event) => {
            event.currentTarget.src = FALLBACK;
          }}
        />

        <div className="mt-4 flex items-center justify-center gap-3 text-sm font-bold text-white/85">
          <span>
            {index + 1} / {photos.length}
          </span>

          {currentPhoto?.titre && (
            <span className="hidden text-white/60 sm:inline">
              — {currentPhoto.titre}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LastGallerie() {
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const navigate = useNavigate();
  const { user } = useUser();

  const orderPhotos = useCallback((photos = []) => {
    const arr = Array.isArray(photos) ? [...photos] : [];

    arr.sort((a, b) => {
      const positionA = Number.isFinite(Number(a?.position))
        ? Number(a.position)
        : Number.MAX_SAFE_INTEGER;

      const positionB = Number.isFinite(Number(b?.position))
        ? Number(b.position)
        : Number.MAX_SAFE_INTEGER;

      return positionA - positionB || (a?.id ?? 0) - (b?.id ?? 0);
    });

    return arr.filter((photo) => isActive(photo?.is_active));
  }, []);

  const getCoverPhoto = useCallback(
    (gallery) => {
      const ordered = orderPhotos(gallery?.photos || []);
      return ordered.find((photo) => photo?.is_cover === true) || ordered[0] || null;
    },
    [orderPhotos]
  );

  useEffect(() => {
    let mounted = true;

    const fetchGalleries = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const all = await getAllGalleries();

        const active = (Array.isArray(all) ? all : [])
          .filter((gallery) => isActive(gallery?.is_active))
          .slice(0, MAX_HOME_ALBUMS);

        if (!mounted) return;

        setGalleries(active);
      } catch (error) {
        console.error("Failed to fetch galleries", error);

        if (mounted) {
          setGalleries([]);
          setErrorMsg("Impossible de charger les albums photos.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchGalleries();

    return () => {
      mounted = false;
    };
  }, []);

  const selectedPhotos = useMemo(() => {
    return orderPhotos(selectedGallery?.photos || []);
  }, [selectedGallery, orderPhotos]);

  const previewLightboxPhotos = useMemo(() => {
    return selectedPhotos.slice(0, MAX_PREVIEW_PHOTOS);
  }, [selectedPhotos]);

  const openAlbumPreview = (gallery, startIndex = 0) => {
    setSelectedGallery(gallery);
    setLightboxIndex(startIndex);
    setLightboxOpen(false);
  };

  const closeAlbumPreview = () => {
    setSelectedGallery(null);
    setLightboxOpen(false);
    setLightboxIndex(0);
  };

  const openLightbox = (index = 0) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const prevPhoto = useCallback(() => {
    const total = previewLightboxPhotos.length;
    if (!total) return;

    setLightboxIndex((currentIndex) => (currentIndex - 1 + total) % total);
  }, [previewLightboxPhotos.length]);

  const nextPhoto = useCallback(() => {
    const total = previewLightboxPhotos.length;
    if (!total) return;

    setLightboxIndex((currentIndex) => (currentIndex + 1) % total);
  }, [previewLightboxPhotos.length]);

  const handleOpenAlbumPage = (galleryId) => {
    navigate(`/galerie-photos/album/${galleryId}`);
  };

  return (
    <section
      id="albums"
      className="relative scroll-mt-[calc(var(--site-navbar-h)+24px)] overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaCameraRetro />
            Galerie du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Derniers{" "}
            <span className="text-primary">albums photo</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Revivez les entraînements, compétitions, événements et moments forts
            du club.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {loading &&
            Array.from({ length: 3 }).map((_, index) => (
              <GallerySkeletonCard key={`gallery-skeleton-${index}`} />
            ))}

          {!loading && errorMsg && <ErrorState message={errorMsg} />}

          {!loading && !errorMsg && galleries.length === 0 && <EmptyState />}

          {!loading &&
            !errorMsg &&
            galleries.map((gallery, index) => {
              const photos = orderPhotos(gallery?.photos || []);
              const cover = getCoverPhoto(gallery);
              const coverSrc = cover?.photo_url
                ? resolvePhoto(cover.photo_url)
                : FALLBACK;

              return (
                <GalleryCard
                  key={gallery.id || index}
                  gallery={gallery}
                  photos={photos}
                  cover={coverSrc}
                  index={index}
                  user={user}
                  onPreview={openAlbumPreview}
                  onOpenAlbum={handleOpenAlbumPage}
                />
              );
            })}
        </div>
      </div>

      {selectedGallery && (
        <AlbumPreviewModal
          gallery={selectedGallery}
          photos={selectedPhotos}
          user={user}
          onClose={closeAlbumPreview}
          onOpenAlbum={handleOpenAlbumPage}
          onOpenLightbox={openLightbox}
        />
      )}

      {selectedGallery && lightboxOpen && previewLightboxPhotos.length > 0 && (
        <Lightbox
          gallery={selectedGallery}
          photos={previewLightboxPhotos}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevPhoto}
          onNext={nextPhoto}
        />
      )}
    </section>
  );
}